import React, { useCallback, useContext } from 'react';
import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { flowRight } from 'lodash';
import { CapTableDetailsModel, CompGroupListCreate } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withAction from 'components/WithAction/withAction';
import withDialog from 'components/WithDialog/withDialog';
import withLink from 'components/WithLink/withLink';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { CompGroupsContext } from 'context';
import { SelectValueViewerProps } from './SelectValueViewer.props';
import TextCell from './TextCell/TextCell';
import { getOptionSelected } from './utilities/getOptionSelected';
import { CustomSelectValueViewerProps } from './utilities/utilities.props';

const SelectValueViewer = (props: SelectValueViewerProps) => {
  const { value, cell, onDoubleClick, options, readOnly = false, useAliasList, useLabel, caseInsensitiveMatch } = props;

  const { format } = useContext(FeaturedSpreadsheetContext);
  const currency = format?.currency;

  const [{ captableInfo, fundList, capTableList }] = useStore() as unknown as UseStoreValues;

  const compGroups = useContext(CompGroupsContext);

  const isReadOnly = Boolean(cell.readOnly || readOnly);

  // Spreadsheet conditions mutate cell metadata in place. Derive the label on
  // each render so an editable cell cannot retain its earlier readonly label.
  const shouldUseOptionMapper = cell.columnLegend || cell.insideLedger || cell.dropdown;
  const selected = shouldUseOptionMapper
    ? getOptionSelected({
      dataProps: {
        props: props as unknown as CustomSelectValueViewerProps,
        fundList: fundList || [],
        captableInfo: captableInfo as CapTableDetailsModel,
        capTableList: capTableList || [],
        useLabel,
        caseInsensitiveMatch,
        group: compGroups?.group as CompGroupListCreate,
        currency,
        defaultOptionList: options,
      },
      useAliasList,
    })
    : value;

  let className = 'value-viewer';

  // A workbook can change a cell's key without replacing the cell object.
  let key = selected;
  if (cell.getFullKey) {
    key = cell.getFullKey();
  } else if (cell.key) {
    key = `${selected}-${cell.key}`;
  }
  const id = `select-value-viewer-${key}`;

  // Custom double click handler for cells not in a spreadsheet component
  const handleDoubleClick = useCallback(() => {
    const event = new MouseEvent('dblclick', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    document.getElementById(id)?.dispatchEvent(event);
  }, [id]);

  if (isReadOnly) {
    return (
      <Grid component="span" className={clsx(className, 'select-value-viewer')}>
        <TextCell isReadOnly={isReadOnly} hasValidationTooltip={Boolean(cell.tooltipMessages?.length)}>
          {selected}
        </TextCell>
      </Grid>
    );
  }

  className = 'select-value-viewer';

  // When a modifier key is held, defer to parent handlers (e.g. AI popover) instead of
  // entering edit mode. This is a safe no-op for non-AI cells.
  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    (onDoubleClick || handleDoubleClick)();
  };

  return (
    <Grid
      className={className}
      id={id}
      onClick={handleClick}
      onKeyDown={onDoubleClick}
      role="button"
      tabIndex={0}>
      <TextCell isReadOnly={isReadOnly} hasValidationTooltip={Boolean(cell.tooltipMessages?.length)}>
        {selected}
      </TextCell>

      <Grid component="span" className="dropdown-icon">
        <ArrowDropDownIcon />
      </Grid>
    </Grid>
  );
};

const enhance = flowRight(withTooltip, withDialog, withRequired, withLink, withAction);

export default enhance(SelectValueViewer);
